import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '@kalos/ui';
import { type ReactNode } from 'react';

import { phoneFullScreenDialogClass } from './phoneFullScreenDialog';

type FieldFullScreenDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** The worded way out at the top: Cancel for a form, Close for something to read, Back, or
   * End tour for a walkthrough. */
  closeLabel?: 'Cancel' | 'Close' | 'Back' | 'End tour';
  /** While true (a form is saving), it can't be closed. */
  isBusy?: boolean;
  /** Classes for the scrolling body. */
  bodyClassName?: string;
  children: ReactNode;
};

/**
 * A long task on a phone or tablet (a form with many fields, several steps, a list to manage):
 * the whole screen, rising from the bottom, with its title and a worded Cancel, Close or Back at
 * the top instead of a corner X. Field layout only; desktop keeps its own dialog.
 */
export const FieldFullScreenDialog = ({
  open,
  onOpenChange,
  title,
  description,
  closeLabel = 'Cancel',
  isBusy = false,
  bodyClassName = 'p-4',
  children,
}: FieldFullScreenDialogProps) => {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isBusy) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showClose={false}
        // The keyboard re-centring would pull a full-screen surface down off the top.
        keepFocusVisibleAboveKeyboard={false}
        // No description: tell Radix so, rather than pointing at one that isn't there.
        {...(description ? {} : { 'aria-describedby': undefined })}
        className={`${phoneFullScreenDialogClass} flex flex-col gap-0 overflow-hidden p-0`}
      >
        <div className="flex flex-none items-start justify-between gap-3 border-b px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)]">
          <div className="min-w-0 flex-1 pt-2">
            <DialogTitle className="truncate text-left">{title}</DialogTitle>
            {description && (
              <DialogDescription className="mt-1 text-left">{description}</DialogDescription>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            className="-mr-3 shrink-0"
            disabled={isBusy}
            onClick={() => handleOpenChange(false)}
          >
            {closeLabel}
          </Button>
        </div>
        <div
          // Up and down only: content that's too wide is clipped rather than scrolling sideways.
          className={`min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)] ${bodyClassName}`}
        >
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};
